import type React from 'react';
import {useMemo, useState} from 'react';
import {Alert, Box} from '@mui/material';
import type {GridColDef, GridPaginationModel, GridSortModel} from '@mui/x-data-grid-premium';
import {useOutletContext} from 'react-router';
import {CustomDataGrid} from '~/components/data-grid/CustomDataGrid';
import {formatDate} from '~/components/data-grid/cells/gridCellFormat';
import {useProfileTransactionsQuery} from '../../hooks/useCategoryProfileQueries';
import type {TransactionRow} from '../../types/categoryProfileTypes';
import {toOrdering} from '../../domain/profileTables';
import {ProfileSection} from '../components/ProfileSection';
import type {CategoryProfileOutletContext} from '../profileTypes';

/** Transactions tab (spec 4.8): the category's lines, paged and sorted on the server. */
export const TransactionsTab: React.FC = () => {
    const {databagId, path, header, money} = useOutletContext<CategoryProfileOutletContext>();
    const [pagination, setPagination] = useState<GridPaginationModel>({page: 0, pageSize: 10});
    const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'amount', sort: 'desc'}]);
    const {data, isFetching, isError} = useProfileTransactionsQuery(databagId, {
        path, ordering: toOrdering(sortModel), page: pagination.page, pageSize: pagination.pageSize, search: '',
    });

    const columns: GridColDef<TransactionRow>[] = useMemo(() => [
        {field: 'materialId', headerName: 'Material ID', width: 140, sortable: false},
        {field: 'description', headerName: 'Description', flex: 1, minWidth: 240, sortable: false,
            valueFormatter: (v: string) => v || '—'},
        {field: 'vendorName', headerName: 'Vendor', width: 200, sortable: false},
        {field: 'site', headerName: 'Site', width: 140, sortable: false},
        {field: 'date', headerName: 'Date', width: 120, valueFormatter: (v: string | null) => formatDate(v)},
        {field: 'quantity', headerName: 'Quantity', type: 'number', width: 110, sortable: false},
        {field: 'amount', headerName: 'Amount', type: 'number', width: 130, valueFormatter: (v: number) => money(v)},
        {field: 'avgUnitCost', headerName: 'Avg. unit cost', type: 'number', width: 130, sortable: false,
            valueFormatter: (v: number | null) => v === null ? '—' : money(v)},
    ], [money]);

    if (isError) return <Alert severity="error">Could not load the transactions.</Alert>;
    return (
        <ProfileSection title={`Transactions in ${header.name}`} subtitle="Last 12 months with data">
            <Box sx={{height: 560}}>
                <CustomDataGrid
                    layoutKey="category-profile-transactions"
                    rows={data?.rows ?? []} columns={columns} loading={isFetching} getRowId={r => r.partId}
                    rowCount={data?.count ?? 0}
                    paginationMode="server" sortingMode="server"
                    paginationModel={pagination} onPaginationModelChange={setPagination}
                    sortModel={sortModel} onSortModelChange={m => {
                        setSortModel(m);
                        setPagination(p => ({...p, page: 0}));
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableRowSelectionOnClick showToolbar
                    toolbarProps={{title: `${(data?.count ?? 0).toLocaleString()} transactions`, showFilterPanel: false}}
                    localeText={{noRowsLabel: 'No transactions match.'}}
                />
            </Box>
        </ProfileSection>
    );
};
